import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Chip, Typography, Paper, Tabs, Tab } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useNavigate, useParams } from 'react-router-dom';
import { GridRenderCellParams } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction,
  GroupQueryOptions
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { colorParser } from 'src/components/paginated-data-grid/ColorParser';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType
} from 'src/components/enhanced-table';
import Loader from 'src/components/Loader';

interface OnHand {
  id: string;
  unitId: string;
  storageUnitId: string;
  warehouseId: string;
  boxRefNumber: string;
  quantity: number;
  unit: UnitProps;
  storageUnit: StorageUnitProps;
  warehouse: WarehouseProps | null;
  createdAt: string;
  updatedAt: string;
}
interface UnitProps {
  id: string;
  itemId: string;
  itemName: string;
  barcode: string;
  symbol: string;
  unitOfMeasurement: string;
  isSynced: boolean;
  createdAt: string;
  updatedAt: string;
  onHandQuantity: number;
  expectedQuantity: number;
  itemAdditionalProperties: Record<string, any> | null;
  itemMinNumber?: number | null;
  itemMaxNumber?: number | null;
  sourceRef?: string | null;
}
interface StorageUnitProps {
  id: string;
  code: string;
  name: string;
  description: string | null;
  parentWarehouseId: string;
  parentStorageUnitId: string | null;
  parentWarehouseName: string;
  parentStorageUnitName: string | null;
  isLeaf: boolean;
  level: number;
  storageTypeId: string;
  isActive: boolean;
  storageType: any;
  properties: any;
  createdAt: string;
  updatedAt: string;
  aggregatedUniqueProducts: number;
  aggregatedTotalQuantity: number;
}
interface WarehouseProps {
  id: string;
  code: string;
  name: string;
  virtual: boolean;
  location: string;
  dimensions: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  stats: any;
  firstLevelStorageUnits: any;
}

const LS_KEY = 'client-on-hand-dynamic-prop-keys';

interface TransferHistoryItem {
  id: string;
  unitId: string;
  quantity: number;
  direction: number; // 1=IN, 2=OUT
  sourceType: number;
  sourceId: string;
  destinationType: number;
  destinationId: string;
  boxRefNumber?: string;
  timestamp: string;
  itemName: string;
  unitBarcode: string;
  sourceName: string;
  destinationName: string;
  operationId: string;
  operationType: number;
  quantityDisplay: string;
  directionDisplay: string;
}

type TabValue = 0 | 1;

const ClientReception: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [dynamicColumns, setDynamicColumns] = useState<
    PaginatedDataGridColumn[]
  >([]);
  const [showZeros, setShowZeros] = useState(false);
  const [tabValue, setTabValue] = useState<TabValue>(0);

  const storageUnitIdRef = useRef<string | null>(null);
  const storageUnitNameRef = useRef<string | null>(null);

  const [transferHistory, setTransferHistory] = useState<TransferHistoryItem[]>(
    []
  );
  const [transfersLoading, setTransfersLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const transferColumns: EnhancedTableColumn[] = [
    {
      id: 'timestamp',
      label: 'Date & Time',
      minWidth: 150,
      type: EnhancedTableColumnType.DATE,
      render: (value: string) => (
        <Typography variant="body2">
          {new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </Typography>
      )
    },
    {
      id: 'operationType',
      label: 'Type',
      type: EnhancedTableColumnType.CUSTOM,
      minWidth: 120,
      render: (value: number) => {
        const map = (t: number) => {
          switch (t) {
            case 1:
              return { label: 'Move In', color: 'success' as const };
            case 2:
              return { label: 'Move Out', color: 'warning' as const };
            case 3:
              return { label: 'Reallocation', color: 'secondary' as const };
            case 4:
              return { label: 'Shipment', color: 'info' as const };
            case 5:
              return { label: 'Transfer', color: 'primary' as const };
            default:
              return { label: 'Unknown', color: 'default' as const };
          }
        };
        const info = map(value);
        return (
          <Chip
            label={info.label}
            size="small"
            color={info.color as any}
            variant="outlined"
          />
        );
      }
    },
    {
      id: 'direction',
      label: 'Direction',
      minWidth: 90,
      type: EnhancedTableColumnType.CUSTOM,
      render: (_: any, row: TransferHistoryItem) => {
        const isIn = row.direction === 1;
        return (
          <Chip
            icon={
              isIn ? (
                <ArrowDownwardIcon fontSize="inherit" />
              ) : (
                <ArrowUpwardIcon fontSize="inherit" />
              )
            }
            label={isIn ? 'IN' : 'OUT'}
            size="small"
            color={isIn ? 'success' : 'error'}
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: 20,
              '& .MuiChip-icon': { fontSize: 14 }
            }}
          />
        );
      }
    },
    {
      id: 'quantity',
      label: 'Qty',
      minWidth: 90,
      type: EnhancedTableColumnType.NUMBER,
      render: (value: number, row: TransferHistoryItem) => {
        const isIn = row.direction === 1;
        const sign = isIn ? '+' : '−';
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: isIn ? 'success.main' : 'error.main'
            }}
          >
            {sign}
            {Math.abs(value ?? 0)}
          </Typography>
        );
      }
    },
    {
      id: 'itemName',
      label: 'Item',
      minWidth: 180,
      type: EnhancedTableColumnType.TEXT
    },
    {
      id: 'boxRefNumber',
      label: 'Container',
      minWidth: 140,
      type: EnhancedTableColumnType.CLICKABLE,
      render: (value: string) => (
        <Typography
          variant="body2"
          color={value ? 'primary' : 'text.secondary'}
        >
          {value || 'Independent'}
        </Typography>
      ),
      onClick: (value, row) => {
        if (value) navigate(`/box/${row.boxRefNumber}`);
      }
    },
    {
      id: 'unitDisplay',
      label: 'From/To',
      minWidth: 180,
      type: EnhancedTableColumnType.CUSTOM,
      render: (_: any, row: TransferHistoryItem) => {
        const isIn = row.direction === 1;
        const name = isIn ? row.sourceName : row.destinationName;
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 500 }}
            color="text.primary"
          >
            {name || '—'}
          </Typography>
        );
      }
    }
  ];

  // ===== Helpers for dynamic columns =====
  const canonicalize = (k: string) => (k ?? '').toLowerCase().trim();
  const getCaseInsensitive = (
    obj: Record<string, any> | null | undefined,
    canonicalKey: string
  ) => {
    if (!obj) return null;
    const exact = (obj as any)[canonicalKey];
    if (exact !== undefined) return exact;
    const found = Object.entries(obj).find(
      ([kk]) => canonicalize(kk) === canonicalKey
    );
    return found ? found[1] : null;
  };

  const formatPropertyName = useCallback((key: string): string => {
    const formatted = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
    const special: Record<string, string> = {
      COLOR: 'Color',
      Color: 'Color',
      Brand: 'Brand',
      MainCategory: 'Main Category',
      Category: 'Category',
      Generic: 'Generic Code',
      size: 'Size',
      gender: 'Gender',
      season: 'Season'
    };
    return special[key] || formatted;
  }, []);

  const initialSeenKeys = (() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr)
        ? Array.from(new Set(arr.map((k: string) => canonicalize(k))))
        : [];
    } catch {
      return [];
    }
  })();

  const seenDynamicKeysRef = useRef<Set<string>>(new Set(initialSeenKeys));
  const persistSeenKeys = (set: Set<string>) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(Array.from(set)));
    } catch {}
  };

  const mapFieldForGrouping = useCallback((field: string): string => {
    const c = canonicalize(field);
    if (seenDynamicKeysRef.current.has(c)) return `Unit.Item.json:${c}`;
    const mapping: Record<string, string> = {
      'unit.itemname': 'Unit.Item.Name',
      'unit.barcode': 'Unit.Barcode',
      'unit.symbol': 'Unit.Symbol',
      'unit.unitofmeasurement': 'Unit.UnitOfMeasurement',
      'unit.issynced': 'Unit.IsSynced',
      quantity: 'Quantity',
      boxrefnumber: 'BoxRefNumber',
      'warehouse.name': 'Warehouse.Name',
      'warehouse.code': 'Warehouse.Code',
      'storageunit.code': 'StorageUnit.Code',
      'storageunit.name': 'StorageUnit.Name',
      createdat: 'CreatedAt',
      updatedat: 'UpdatedAt'
    };
    return mapping[c] || field;
  }, []);

  const ALLOWED_DYNAMIC = new Set([
    'brand',
    'color',
    'size',
    'category',
    'maincategory',
    'gender',
    'season',
    'generic'
  ]);
  const shouldKeepKey = (key: string, items: OnHand[]) => {
    let nonEmpty = 0;
    for (const it of items) {
      const v = getCaseInsensitive(
        it.unit?.itemAdditionalProperties ?? undefined,
        key
      );
      if (v !== null && v !== undefined && String(v).trim() !== '') nonEmpty++;
    }
    return nonEmpty >= Math.max(1, Math.floor(items.length * 0.2));
  };

  const buildColumns = useCallback(
    (dynamicKeys: string[]): PaginatedDataGridColumn[] => {
      const base: PaginatedDataGridColumn[] = [
        {
          field: 'unit.barcode',
          headerName: 'Barcode',
          flex: 1,
          minWidth: 130,
          groupable: false,
          valueGetter: (p) => p.row.unit?.barcode,
          renderCell: (p: GridRenderCellParams) => (
            <Typography
              color="primary"
              sx={{ cursor: 'pointer', fontWeight: 'medium' }}
              onClick={() => navigate(`/units/${p.row.unit?.id}`)}
            >
              {p.value || 'N/A'}
            </Typography>
          )
        },
        {
          field: 'unit.itemName',
          headerName: 'Product Name',
          flex: 1.5,
          minWidth: 200,
          groupable: true,
          valueGetter: (p) => p.row.unit?.itemName
        },
        {
          field: 'unit.symbol',
          headerName: 'Symbol',
          flex: 1,
          minWidth: 120,
          groupable: true,
          valueGetter: (p) => p.row.unit?.symbol
        },
        {
          field: 'quantity',
          headerName: 'Quantity',
          width: 100,
          type: 'number',
          groupable: true,
          renderCell: (p: GridRenderCellParams) => (
            <Chip
              label={p.value ?? 0}
              color="primary"
              variant="outlined"
              size="small"
            />
          )
        },
        {
          field: 'boxRefNumber',
          headerName: 'Box Reference',
          flex: 1,
          minWidth: 150,
          groupable: true,
          renderCell: (p: GridRenderCellParams) => (
            <Typography
              sx={{
                cursor: p.value ? 'pointer' : 'default',
                '&:hover': { textDecoration: p.value ? 'underline' : 'none' }
              }}
              onClick={() => p.value && navigate(`/box/${p.value}`)}
            >
              {p.value || '-'}
            </Typography>
          )
        },
        {
          field: 'unit.unitOfMeasurement',
          headerName: 'Unit',
          width: 100,
          groupable: true,
          valueGetter: (p) => p.row.unit?.unitOfMeasurement
        }
      ];

      const dynamicCols = dynamicKeys.sort().map((ck) => {
        const prettyHeader = formatPropertyName(ck);
        const col: PaginatedDataGridColumn = {
          field: ck,
          headerName: prettyHeader,
          flex: 1,
          minWidth: 110,
          valueGetter: (p) =>
            getCaseInsensitive(
              p.row.unit?.itemAdditionalProperties ?? undefined,
              ck
            ),
          sortable: true,
          groupable: true
        };
        if (ck.includes('color')) {
          col.columnType = 'color';
          col.renderCell = (p: GridRenderCellParams) => {
            if (!p.value)
              return <Typography color="text.secondary">-</Typography>;
            const colorValue = colorParser(p.value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: colorValue,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                />
                <Typography variant="body2">{p.value}</Typography>
              </Box>
            );
          };
        }
        if (
          ['category', 'maincategory', 'brand', 'season'].some((f) =>
            ck.includes(f)
          )
        ) {
          col.renderCell = (p: GridRenderCellParams) =>
            p.value ? (
              <Chip
                label={p.value}
                size="small"
                variant="outlined"
                color="primary"
              />
            ) : (
              <Typography color="text.secondary">-</Typography>
            );
        }
        return col;
      });

      const dateColumns: PaginatedDataGridColumn[] = [
        {
          field: 'createdAt',
          headerName: 'Added Date',
          width: 160,
          type: 'dateTime',
          groupable: false
        },
        {
          field: 'updatedAt',
          headerName: 'Last Updated',
          width: 160,
          type: 'dateTime',
          groupable: false
        }
      ];

      return [...base, ...dynamicCols, ...dateColumns];
    },
    [formatPropertyName, navigate]
  );

  useEffect(() => {
    setDynamicColumns(buildColumns([]));
  }, [buildColumns]);

  const fetchUnits = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
      grouping?: GroupQueryOptions;
    }) => {
      if (!clientId) throw new Error('Client ID is required');

      const queryParams = new URLSearchParams({
        clientId: clientId,
        PageNumber: String(params.page + 1),
        PageSize: String(params.pageSize)
      });
      if (params.search) queryParams.append('SearchTerm', params.search);
      if (showZeros) queryParams.append('includeZeros', 'true');

      const isGrouped = params.grouping && params.grouping.GroupBy.length > 0;
      if (isGrouped && params.grouping) {
        params.grouping.GroupBy.forEach((f) =>
          queryParams.append('GroupBy', mapFieldForGrouping(f))
        );
        queryParams.append('Level', String(params.grouping.Level));
        queryParams.append('GroupSortBy', params.grouping.GroupSortBy || 'key');
        queryParams.append(
          'GroupSortDescending',
          String(params.grouping.GroupSortDescending || false)
        );
        queryParams.append(
          'IncludeCount',
          String(params.grouping.IncludeCount || true)
        );
        if (params.grouping.ParentKey)
          queryParams.append('ParentKey', params.grouping.ParentKey);
      } else if (params.sortModel?.length > 0) {
        const { field, sort } = params.sortModel[0];
        const c = canonicalize(field);
        const isDynamic = seenDynamicKeysRef.current.has(c);
        let sortField = field;
        if (isDynamic) sortField = `unit.itemAdditionalProperties.${c}`;
        else if (
          field.startsWith('unit.') ||
          field.startsWith('warehouse.') ||
          field.startsWith('storageUnit.')
        )
          sortField = field;
        queryParams.append('SortBy', sortField);
        queryParams.append('SortDescending', String(sort === 'desc'));
      }

      const endpoint = `api/Client/${clientId}/warehouse-inventory`;
      const res = await DataService.get(
        `${endpoint}?${queryParams.toString()}`
      );
      if (!res.ok)
        throw new Error(
          `HTTP ${res.status}: Failed to fetch client inventory items`
        );
      const json = await res.json();

      const suId = json?.data?.storageUnitId ?? null;
      const suName = json?.data?.storageUnitName ?? null;
      if (suId) storageUnitIdRef.current = suId;
      if (suName) storageUnitNameRef.current = suName;

      let items: OnHand[] = [];
      let groups: any[] = [];

      if (isGrouped) {
        if (json?.data?.groups)
          groups = Array.isArray(json.data.groups)
            ? json.data.groups
            : Object.values(json.data.groups);
        else if (Array.isArray(json?.groups)) groups = json.groups;
      } else {
        if (json?.data?.items)
          items = Array.isArray(json.data.items)
            ? json.data.items
            : Object.values(json.data.items);
        else if (Array.isArray(json?.items)) items = json.items;
      }

      if (!isGrouped && items.length > 0) {
        const presentKeys = new Set<string>();
        for (const u of items) {
          const props = u.unit?.itemAdditionalProperties;
          if (!props || typeof props !== 'object') continue;
          for (const k of Object.keys(props)) presentKeys.add(canonicalize(k));
        }
        let dirty = false;
        for (const ck of presentKeys)
          if (!seenDynamicKeysRef.current.has(ck)) {
            seenDynamicKeysRef.current.add(ck);
            dirty = true;
          }
        if (dirty) persistSeenKeys(seenDynamicKeysRef.current);

        const dyn = Array.from(presentKeys)
          .filter((k) => ALLOWED_DYNAMIC.has(k))
          .filter((k) => shouldKeepKey(k, items))
          .sort();
        setDynamicColumns(buildColumns(dyn));
      }

      return {
        items: isGrouped ? undefined : items,
        groups: isGrouped ? groups : undefined,
        totalCount:
          json?.data?.totalCount ??
          json?.totalCount ??
          (isGrouped ? groups.length : items.length),
        totalGroups: isGrouped
          ? json?.data?.totalGroups ??
            json?.totalGroups ??
            json?.data?.totalCount ??
            json?.totalCount ??
            groups.length
          : undefined,
        pageNumber:
          json?.data?.pageNumber ?? json?.pageNumber ?? params.page + 1,
        pageSize: json?.data?.pageSize ?? json?.pageSize ?? params.pageSize,
        totalPages:
          json?.data?.totalPages ??
          json?.totalPages ??
          Math.ceil(
            (isGrouped
              ? json?.data?.totalGroups ??
                json?.totalGroups ??
                json?.data?.totalCount ??
                json?.totalCount ??
                groups.length
              : json?.data?.totalCount ?? json?.totalCount ?? items.length) /
              params.pageSize
          ),
        hasNext: json?.data?.hasNext ?? json?.hasNext ?? false,
        hasPrevious: json?.data?.hasPrevious ?? json?.hasPrevious ?? false
      };
    },
    [buildColumns, mapFieldForGrouping, clientId, showZeros]
  );

  const loadStorageUnitTransfers = useCallback(async () => {
    const suId = storageUnitIdRef.current;
    if (!suId) return;
    setTransfersLoading(true);
    try {
      const response = await DataService.get(
        `api/Operation/storage-unit/${suId}/transfers?page=1&pageSize=100`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      const items = json?.items ?? json?.data?.items ?? [];
      setTransferHistory(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('Error loading storage unit transfers:', err);
      dispatch(
        showAlert({ message: 'Failed to load transfer history', type: 'error' })
      );
    } finally {
      setTransfersLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (tabValue === 1) {
      if (!storageUnitIdRef.current && clientId) {
        const params = new URLSearchParams({
          clientId,
          PageNumber: '1',
          PageSize: '1'
        });
        DataService.get(
          `api/Client/${clientId}/warehouse-inventory?${params.toString()}`
        )
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => {
            if (j?.data?.storageUnitId)
              storageUnitIdRef.current = j.data.storageUnitId;
            if (j?.data?.storageUnitName)
              storageUnitNameRef.current = j.data.storageUnitName;
          })
          .finally(() => {
            void loadStorageUnitTransfers();
          });
      } else {
        void loadStorageUnitTransfers();
      }
    }
  }, [tabValue, clientId, loadStorageUnitTransfers]);

  const actions: PaginatedDataGridAction[] = [
    {
      key: 'toggle-zeros',
      title: showZeros ? 'Hide Zero Quantities' : 'Show Zero Quantities',
      icon: showZeros ? <FilterAltOffIcon /> : <FilterAltIcon />,
      onClick: () => {
        setShowZeros((p) => !p);
        setRefreshKey((p) => p + 1);
      }
    }
  ];

  return (
    <>
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v: TabValue) => setTabValue(v)}>
          <Tab
            icon={<InventoryIcon />}
            label="Inventory"
            iconPosition="start"
          />
          <Tab
            icon={<SwapHorizIcon />}
            label="Transfers"
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <PaginatedDataGrid
          key={refreshKey}
          title="Client Reception"
          columns={dynamicColumns}
          idField="id"
          useBackendPagination
          onDataFetch={fetchUnits}
          cacheTimeout={300000}
          actions={actions}
          enableGrouping={false}
          enableGroupDrillDown={false}
          fieldMapper={mapFieldForGrouping}
          wmsMode
          densityLevel="standard"
          autoHeight
          compactEmptyState
          responsiveColumns
          hideFooterWhenEmpty={false}
          striped
          maxHeight={700}
          onRowClick={(p) => navigate(`/units/${p.row.unit?.id}`)}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{
            '& .MuiDataGrid-cell': {
              borderRight: '1px solid',
              borderColor: 'divider'
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.default'
            }
          }}
        />
      )}

      {tabValue === 1 && (
        <Paper sx={{ p: 2 }}>
          {!storageUnitIdRef.current ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="240px"
            >
              <Loader />
            </Box>
          ) : transfersLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="240px"
            >
              <Loader />
            </Box>
          ) : transferHistory.length > 0 ? (
            <EnhancedTable
              data={transferHistory}
              columns={transferColumns}
              title=""
              dense
            />
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="240px"
            >
              <Typography variant="body2" color="text.secondary">
                No transfer history available
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </>
  );
};

export default ClientReception;
